import axios from 'axios';
import type {
  LoginCredentials,
  RegisterCredentials,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
  User
} from '@/types/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
// Auth API endpoints
const AUTH_ENDPOINTS = {
  REGISTER: '/merchant/v1/auth/signup/',
  LOGIN: '/merchant/v1/auth/login/',
  LOGOUT: '/merchant/v1/auth/logout/',
  FORGOT_PASSWORD: '/merchant/v1/auth/forgot-password/',
  RESET_PASSWORD: '/merchant/v1/auth/reset-password/',
  CHANGE_PASSWORD: '/merchant/v1/auth/change-password/',
  PROFILE_UPDATE: '/merchant/v1/auth/update-profile/',
  //PROFILE_GET: '/merchant/v1/auth/profile/',
  ACCOUNT_ACTIVATION: '/merchant/v1/auth/account-activation/',
  LOGIN_AS: '/backoffice/api/v1/merchants',

  //ME: '/merchant/v1/team-session/verify/',
  //REFRESH: '/merchant/v1/team-session/verify/',
} as const;
import { clearAuthCookiesService } from './clearAuthCookiesService';


class AuthService {
  private apiClient;
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },

      // Enable cookies with credentials
      timeout: 10000,

    });
    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Clear only auth-related stored auth data
          this.clearStoredAuth();
          // Emit auth logout event
          window.dispatchEvent(new CustomEvent('auth:authLogout'));
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<any> {
    try {
      const response = await this.apiClient.post(AUTH_ENDPOINTS.LOGIN, credentials);
      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Login failed');
    }
  }

  async loginUserAs(credentials:any): Promise<any> {
    try {
      const response = await this.apiClient.post(`/backoffice/api/v1/merchants/${credentials}/login-as/`);
      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Login failed');
    }
  }

  /**
   * Register new user
   */
  async register(credentials: RegisterCredentials): Promise<any> {
    try {
      const response = await this.apiClient.post(
        AUTH_ENDPOINTS.REGISTER,
        credentials
      );

      const authData = response;

      return authData;
    } catch (error) {

      throw this.handleAuthError(error, 'Registration failed');
    }
  }

  async changePassword(data: ChangePasswordRequest): Promise<any> {
    const payload = {
      old_password: data.currentPassword,
      new_password: data.newPassword
    }
    try {
      const response = await this.apiClient.post(
        AUTH_ENDPOINTS.CHANGE_PASSWORD,
        payload
      );

      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Failed to change password');
    }
  }

  /**
   * Activate account with token
   */
  async activateAccount(token: string): Promise<any> {
    const data = {
      "token": token
    }
    try {
      const response = await this.apiClient.post(AUTH_ENDPOINTS.ACCOUNT_ACTIVATION, data);
      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Team login failed');
    }
  }

  /**
   * Refresh authentication session
   * With cookie-based auth, the server refreshes the cookie automatically
   * We just need to update our local user data
   */
  // async refreshToken(): Promise<AuthResponse> {
  //   try {
  //     const response = await this.apiClient.post<AuthResponse>(AUTH_ENDPOINTS.REFRESH);

  //     const authData = response.data;

  //     return authData;
  //   } catch (error) {
  //     this.clearStoredAuth();
  //     throw this.handleAuthError(error, 'Session refresh failed')token;
  //   }
  // }

  /**
   * Get current user profile
   */
  // async getCurrentUser(): Promise<User> {
  //   try {
  //     const response = await this.apiClient.get(AUTH_ENDPOINTS.PROFILE_GET);
  //     // Handle different response structures
  //     let userData = response.data;

  //     // Some APIs wrap payload under data.data; support both
  //     if (userData && typeof userData === 'object') {
  //       userData = userData.data ?? userData;
  //     }

  //     // Ensure we have a valid user object
  //     if (!userData || typeof userData !== 'object') {
  //       throw new Error('Invalid user data received');
  //     }

  //     return userData as User;
  //   } catch (error: any) {
  //     // Re-throw with more context
  //     if (error.response?.status === 401) {
  //       // 401 means not authenticated - this is expected in some cases
  //       throw error;
  //     }
  //     throw this.handleAuthError(error, 'Failed to get user profile');
  //   }
  // }

  /**
   * Fetch profile from dedicated endpoint
   */
  // async getProfile(): Promise<any> {
  //   try {
  //     const response = await this.apiClient.get(AUTH_ENDPOINTS.PROFILE_GET);
  //     return response;
  //   } catch (error) {
  //     throw this.handleAuthError(error, 'Failed to fetch profile');
  //   }
  // }

  /**
   * Update user profile
   */
  // async updateProfile(data: UpdateProfileRequest): Promise<any> {
  //   try {
  //     const response = await this.apiClient.patch(AUTH_ENDPOINTS.PROFILE_UPDATE, data);
  //     //const user: User = (response.data && (response.data.data ?? response.data)) as User;
  //     //localStorage.setItem('auth_user', JSON.stringify(user));
  //     return response;
  //   } catch (error) {
  //     throw this.handleAuthError(error, 'Failed to update profile');
  //   }
  // }

  /**
   * Forgot password request
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    try {
      await this.apiClient.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, data);
    } catch (error) {
      throw this.handleAuthError(error, 'Failed to send password reset email');
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    const payload = {
      confirm_password: data.confirmPassword,
      new_password: data.password,
    };
    try {
      const url = `${AUTH_ENDPOINTS.RESET_PASSWORD}?token=${encodeURIComponent(data.token)}`;
      await this.apiClient.post(url, payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || error.response?.data?.message;
        if (errorMessage === 'Reset link has already been used') {
          throw new Error('This reset link has already been used. Please request a new password reset.');
        }
      }
      throw this.handleAuthError(error, 'Failed to reset password');
    }
  }

  /**
   * Check if user is authenticated
   * This now relies on cookies to verify authentication status
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      // Try to get current user to verify authentication
      //await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get stored authentication data
   * This method is deprecated - auth state should be managed by Redux
   */
  getStoredAuth(): { user: User | null; token: string | null } {
    // Return empty data - auth state should be managed by Redux
    return { user: null, token: null };
  }

  /**
   * Logout user: clear server session and client data
   */
  async authLogout(): Promise<void> {
    try {
      // Call logout endpoint to clear server-side session
      await this.apiClient.post(AUTH_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.warn('Server logout failed:', error);
    }
    
    // Always clear client-side data regardless of server response
    clearAuthCookiesService.clearAuthCookies();
    this.clearStoredAuth();
  }

  /**
   * Clear only auth-related stored authentication data
   * This method is deprecated - auth state should be managed by Redux
   */
  private clearStoredAuth(): void {
    // No longer clearing localStorage - auth state managed by Redux
    // This method is kept for compatibility but does nothing
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     error.response?.data?.errors?.email || 
                     error.message || 
                     defaultMessage;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error(defaultMessage);
  }
}


// Create singleton instance
export const authService = new AuthService();

// Export for testing
export { AuthService };
